import { OrthographicCamera, Vector3 } from 'three';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { Axis } from '../../renderers/side-view/side-view.component';
import { CUBE_SIZE } from '../../state/cuboid-location-state-3d';
import { EditSource } from '../../state/render-state';
import {
  buildCuboidPointsFromMesh,
  getFaceDimensions,
} from '../../utils/cuboid-utils';
import {
  getZInViewCoordinates,
  TentPoleVertex,
  vectorToPoint,
} from '../../utils/point-cloud-utils';
import {
  Plane,
  SideViewResizeControls,
} from '../controls/side-view-resize-controls';
import { CuboidHelper } from '../mesh/cuboid/cuboid-helper';
import { getSideViewRendererLayers, setRendererLayers } from '../render-utils';

export class SideViewCamera extends OrthographicCamera {
  constructor(private axis: Axis) {
    super(0, 0, 0, 0);
    setRendererLayers([getSideViewRendererLayers()[this.axis]], this);
    this.userData = { meshType: 'OrthographicCamera' };
  }

  updateLocation(
    focusedCuboid: CuboidHelper,
    editSource: EditSource | undefined,
    controls: SideViewResizeControls,
    sideViewSize: Point2D,
  ) {
    // Update the mesh and the aspect only when edits are coming from
    // something OTHER than the current side view. This will feel more
    // like the point cloud moving behind the cuboid. If it's from the
    // current side view, we want to give more the feel of the cuboid
    // moving around in the side view.
    if (editSource?.index !== this.axis) {
      controls.attach(focusedCuboid.box);
      const viewAspect = sideViewSize[0] / sideViewSize[1];
      this.updateCameraAspect(focusedCuboid.box.scale, viewAspect);
    }
    this.updateCameraPosition(focusedCuboid, controls.plane);
  }

  updateCameraPosition(cuboid: CuboidHelper, plane: Plane) {
    const cuboidPoints = buildCuboidPointsFromMesh(cuboid.box);

    // Cuboid vertex closest to each camera in the 3 side-views
    const cameraZ = getZInViewCoordinates(
      TentPoleVertex.BackTopLeft,
      cuboidPoints,
      plane,
    );
    // Cuboid vertex furthest from each camera in the 3 side-views
    const farZ = getZInViewCoordinates(
      TentPoleVertex.FrontBottomRight,
      cuboidPoints,
      plane,
    );

    // Set the camera's view slightly larger than the cuboid, so that rounding
    // errors don't place the camera inside the cuboid. This fixes a bug with
    // raycasting that intermittently prevented dragging the cuboid.
    this.position.z = cameraZ + 1e-5;
    this.near = 0;
    if (this.axis === 2) {
      this.rotation.set(0, 0, Math.PI);
    }
    this.far = cameraZ - farZ + 2e-5;
    this.updateProjectionMatrix();
  }

  updateCameraAspect(scale: Vector3, viewAspect: number) {
    const pointScale = vectorToPoint(scale);
    const [widthOffset, heightOffset] = this.getCameraDimensions(
      pointScale,
      viewAspect,
    );

    this.left = -widthOffset;
    this.right = widthOffset;
    this.top = heightOffset;
    this.bottom = -heightOffset;
  }

  private getCameraDimensions(
    scale: Point3D,
    viewAspect: number,
  ): [number, number] {
    const [scaleX, scaleY] = getFaceDimensions(scale, this.axis);
    const faceAspect = scaleX / scaleY;

    const VIEW_BUFFER_FACTOR = 1.2;
    const CUBE_HALF = CUBE_SIZE / 2;
    let widthOffset;
    let heightOffset;
    if (faceAspect > viewAspect) {
      widthOffset = scaleX * CUBE_HALF * VIEW_BUFFER_FACTOR;
      heightOffset = widthOffset / viewAspect;
    } else {
      heightOffset = scaleY * CUBE_HALF * VIEW_BUFFER_FACTOR;
      widthOffset = heightOffset * viewAspect;
    }

    return [widthOffset, heightOffset];
  }
}
